import { ViewWidget } from '../view/ViewWidget.js';
import { MaskWidget } from './MaskWidget.js';
import { Widget } from '../core/Widget.js';
import { Validation } from '../core/Validation.js';

/**
 * Виджет числового ввода (порт jQuery-реализации без префикса "im").
 * Поведение сохранено:
 *  - замена ',' на '.'
 *  - валидация по scale/positive/min/max
 *  - сравнение с полями, указанными в data-spf / data-epf
 *
 * Требования проекта:
 *  - работать с data-* через dataset
 *  - не добавлять новую функциональность
 *  - приватные хэндлеры располагать рядом с методами, где используются
 *  - _onDestroy — в конце класса
 */
class NumboxWidget extends Widget {
    constructor() {
        super(...arguments);
        /**
         * Обработчик ввода:
         * - нормализует значение (',' → '.')
         * - запускает валидацию
         * @param e Событие input
         */
        this._onInput = (e) => {
            const el = e.target;
            // Нормализация: запятая → точка, как в исходнике
            el.value = el.value.replaceAll(',', '.');
            // безопасная синхронизация пары: запускаем сразу оба
            const perEl = this._resolvePair(el);
            if (perEl) {
                Validation.run([el, perEl]); // ← никакой рекурсии: validate() не вызывает run()
            }
            else {
                Validation.run(el);
            }
        };
    }
    /** Ключевой класс для поиска виджета */
    static _cssKey() {
        return 'im-numbox';
    }
    /**
     * Инициализация виджета: подвешивает обработчики.
     * @param op Опции виджета
     */
    _create(op) {
        // сохраняем защищённую копию; опциональные поля могут быть не заданы
        this._op = { ...(op ?? {}) };
        try {
            const allowMinus = this._op.positive !== true;
            const minus = allowMinus ? '\\-' : '';
            MaskWidget.getOrInit(this.element, {
                mask: '#',
                tokens: { '#': { pattern: new RegExp(`[0-9\\.${minus},]`), recursive: true } }
            });
        }
        catch (ex) {
            console.error(ex);
        }
        this.element.addEventListener('input', this._onInput);
        this.element.addEventListener('change', this._onInput);
    }
    _resolvePair(el) {
        // TODO: Проверить - можно ли перевести на _op;
        const perKey = el.dataset.spf ?? el.dataset.epf;
        return perKey
            ? ViewWidget.closestView(el)?.findElementByDataKey(perKey)
            : undefined;
    }
    /**
     * Контракт Validatable: Проверка элемента
     * @returns - результат проверки (ValidationResult)
     */
    validate() {
        const ok = this._validate(this.element);
        return ok ? { ok: true } : { ok: false };
    }
    /**
     * Внутренняя проверка значения (порт оригинальной _validate).
     * @param el HTMLInputElement | HTMLTextAreaElement с data-атрибутами настройки
     * @returns true — если корректно; иначе false
     */
    _validate(el) {
        const str = el.value.trim();
        if (str === '')
            return true;
        const scale = this._op.scale ?? 0;
        const positive = this._op.positive === true;
        const norm = str.replace(',', '.');
        // Регэкспы как в исходнике (допускают "10." при scale>0)
        const rNegInt = /^-\d+$/;
        const rPosInt = /^\d+$/;
        const rPosFrac = new RegExp(`^\\d+\\.\\d{0,${scale}}$`);
        const rNegFrac = new RegExp(`^-\\d+\\.\\d{0,${scale}}$`);
        if (!positive) {
            if (scale > 0) {
                if (!(rPosFrac.test(norm) || rNegFrac.test(norm) || rNegInt.test(norm) || rPosInt.test(norm)))
                    return false;
            }
            else {
                if (!(rNegInt.test(norm) || rPosInt.test(norm)))
                    return false;
            }
        }
        else {
            if (scale > 0) {
                if (!(rPosFrac.test(norm) || rPosInt.test(norm)))
                    return false;
            }
            else {
                if (!rPosInt.test(norm))
                    return false;
            }
        }
        const val = scale > 0 ? parseFloat(norm) : parseInt(norm, 10);
        if (!Number.isFinite(val))
            return false;
        // min/max из опций
        const { min, max } = this._op;
        if (min !== undefined && min > val)
            return false;
        if (max !== undefined && max < val)
            return false;
        // epf: текущее >= другого
        if (this._op.epf) {
            const otherStr = ViewWidget.closestView(el)
                ?.findElementByDataKey(this._op.epf)?.value ?? '';
            if (otherStr !== '') {
                const other = scale > 0 ? parseFloat(otherStr.replace(',', '.')) : parseInt(otherStr, 10);
                if (!Number.isFinite(other))
                    return false;
                return val >= other;
            }
            return true;
        }
        // spf: текущее <= другого
        if (this._op.spf) {
            const otherStr = ViewWidget.closestView(el)
                ?.findElementByDataKey(this._op.spf)?.value ?? '';
            if (otherStr !== '') {
                const other = scale > 0 ? parseFloat(otherStr.replace(',', '.')) : parseInt(otherStr, 10);
                if (!Number.isFinite(other))
                    return false;
                return val <= other;
            }
            return true;
        }
        return true;
    }
    /**
     * Вернуть основное значение виджета.
     */
    getValue() {
        const raw = this.element.value.trim();
        if (raw === "")
            return null;
        const value = raw.replace(/,/g, ".");
        const val = parseFloat(value);
        if (!Number.isFinite(val))
            return null;
        return val;
    }
    /**
     * Установить основное значение
     * @param val - устанавливаемое значение
     */
    setValue(val) {
        const el = this.element;
        // Нормализация к строке
        let s = "";
        if (val === null || val === undefined) {
            s = "";
        }
        else if (typeof val === "number") {
            s = String(val).replace(/,/g, ".");
        }
        else {
            const str = String(val).trim();
            s = (str === "" || str.toLowerCase() === "dbnull")
                ? ""
                : str.replace(/,/g, ".");
        }
        // Ранний выход, если не меняется
        if (el.value === s) {
            return true;
        }
        // Записать в элемент
        el.value = s;
        // Валидировать текущий и парный
        const perEl = this._resolvePair(el);
        if (perEl) {
            Validation.run([el, perEl]);
        }
        else {
            Validation.run(el);
        }
        return true;
    }
    /** true — если виджет считает, что значение задано */
    hasValue() {
        return this.element.value !== '';
    }
    /**
     * Очистка ресурсов. Вызывается родителем (Widget → DomReaper) при удалении узла из DOM.
     * Идемпотентна.
     */
    _onDestroy() {
        this.element.removeEventListener('input', this._onInput);
        this.element.removeEventListener('change', this._onInput);
    }
}
/** Имя виджета */
NumboxWidget._alias = "Numbox";

export { NumboxWidget };
